"use client";

import {
  useEffect,
  useState,
  useCallback,
  useRef,
  useLayoutEffect,
} from "react";
import { useParams } from "react-router-dom";
import { Toaster } from "react-hot-toast";
import { showSuccess, showError, showWarning } from "../lib/toast";
import {
  publicTestApi,
  AgentDetailPublic,
  OutboundCallResponse,
} from "../lib/api";
import SmallHeader from "../../components/SmallHeader";
import { publicCallingApi } from "../lib/teleserver_api";
import { getDemoFlowsByGroupLink } from "../../services/flowService";
import {
  getProdServicesStatus,
  getProdServicesStatusFromTeleserver,
  startProdServices,
  teleserverUrlFromStatus,
} from "../../services/configService";
import RefreshIcon from "../../assets/RefreshIcon";
import LoadingSpinner from "../../assets/LoadingSpinner";
import {
  getAutoStopWarning,
  isProdServicesUp,
  shouldPollProdServicesStatus,
  STATUS_POLL_INTERVAL_MS,
  STATUS_POLL_WINDOW_MS,
} from "../../utils/prodServices";

/** Max height (px) an auto-sized textarea can grow to before it starts scrolling. */
const TEXTAREA_MAX_HEIGHT = 420;

/**
 * Textarea that grows/shrinks with its content so the whole value stays visible,
 * capped at TEXTAREA_MAX_HEIGHT (scrolls beyond that). Purely visual - behaves
 * like a plain controlled textarea otherwise.
 */
function AutoResizeTextarea({
  value,
  onChange,
  placeholder,
  className,
}: {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  placeholder?: string;
  className?: string;
}) {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const resize = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    // Reset first so the element can shrink as well as grow.
    el.style.height = "auto";
    const contentHeight = el.scrollHeight;
    el.style.height = `${Math.min(contentHeight, TEXTAREA_MAX_HEIGHT)}px`;
    el.style.overflowY =
      contentHeight > TEXTAREA_MAX_HEIGHT ? "auto" : "hidden";
  }, []);

  // Size on mount and whenever the value changes.
  useLayoutEffect(() => {
    resize();
  }, [value, resize]);

  // Line wrapping (and therefore height) changes with the viewport width.
  useEffect(() => {
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, [resize]);

  // Web fonts can load after the first measurement and change the height.
  useEffect(() => {
    (document as any).fonts?.ready?.then(resize).catch(() => {});
  }, [resize]);

  return (
    <textarea
      ref={textareaRef}
      rows={1}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className={className}
    />
  );
}

/** Joins a multi-select's picks into the one value the call receives. */
const MULTI_SELECT_SEPARATOR = ", ";

/** Max height (px) of the multi-select option list before it scrolls. */
const MULTI_SELECT_PANEL_MAX_HEIGHT = 240;

function parseMultiSelectValue(value: string): string[] {
  return value
    .split(",")
    .map((v) => v.trim())
    .filter((v) => v !== "");
}

/**
 * Dropdown with a checkbox per option, for a select variable that allows
 * multiple selections. The value is the picks joined by commas, in option
 * order ("a, b"), which is the form the call takes them in.
 */
function MultiSelectDropdown({
  options,
  value,
  placeholder,
  onChange,
}: {
  options: string[];
  value: string;
  placeholder: string;
  onChange: (value: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [placement, setPlacement] = useState({
    up: false,
    maxHeight: MULTI_SELECT_PANEL_MAX_HEIGHT,
  });
  const rootRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  // The trigger shows the value exactly as it will be sent. A pick that is no
  // longer an option (old default) has no checkbox, so it drops on the next tick.
  const picked = parseMultiSelectValue(value);

  // Open downward unless there's no room: the form scrolls inside an overflow
  // container that clips the list, and the last fields sit right above the
  // sticky Start Call bar. Measured before paint, so the list never jumps.
  useLayoutEffect(() => {
    const trigger = triggerRef.current?.getBoundingClientRect();
    const panel = panelRef.current;
    if (!open || !trigger || !panel) return;
    let top = 0;
    let bottom = window.innerHeight;
    for (let el = rootRef.current?.parentElement; el; el = el.parentElement) {
      if (/(auto|scroll|hidden)/.test(getComputedStyle(el).overflowY)) {
        const rect = el.getBoundingClientRect();
        top = Math.max(top, rect.top);
        bottom = Math.min(bottom, rect.bottom);
      }
    }
    const below = bottom - trigger.bottom - 8;
    const above = trigger.top - top - 8;
    const needed = Math.min(panel.scrollHeight, MULTI_SELECT_PANEL_MAX_HEIGHT);
    const up = below < needed && above > below;
    setPlacement({
      up,
      maxHeight: Math.min(MULTI_SELECT_PANEL_MAX_HEIGHT, up ? above : below),
    });
  }, [open]);

  // While open: close on a click outside, or on Escape (focus goes back to
  // the trigger so keyboard users don't lose their place).
  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      setOpen(false);
      triggerRef.current?.focus();
    };
    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const toggle = (option: string) => {
    const next = new Set(picked);
    if (next.has(option)) next.delete(option);
    else next.add(option);
    onChange(options.filter((o) => next.has(o)).join(MULTI_SELECT_SEPARATOR));
  };

  return (
    <div
      ref={rootRef}
      className="relative"
      // Tabbing past the last option closes the list, like a native select.
      // Mouse clicks elsewhere are the mousedown listener's job (relatedTarget
      // is null for those), so this never closes the list mid-click.
      onBlur={(e) => {
        const next = e.relatedTarget as Node | null;
        if (next && !rootRef.current?.contains(next)) setOpen(false);
      }}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none flex items-center justify-between gap-2 text-left">
        <span
          className={`min-w-0 break-words ${
            picked.length ? "" : "text-gray-500 dark:text-gray-400"
          }`}>
          {picked.length ? picked.join(MULTI_SELECT_SEPARATOR) : placeholder}
        </span>
        <svg
          className={`w-4 h-4 shrink-0 text-gray-500 transition-transform ${
            open ? "rotate-180" : ""
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {open && (
        <div
          ref={panelRef}
          role="group"
          aria-label={placeholder}
          style={{ maxHeight: placement.maxHeight }}
          className={`absolute z-20 w-full overflow-y-auto bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg py-1 ${
            placement.up ? "bottom-full mb-1" : "top-full mt-1"
          }`}>
          {options.map((opt) => (
            <label
              key={opt}
              className="flex items-center gap-2.5 px-3 py-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600">
              <input
                type="checkbox"
                checked={picked.includes(opt)}
                onChange={() => toggle(opt)}
                className="w-4 h-4 rounded border-gray-300 dark:border-gray-500 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-gray-900 dark:text-white break-words min-w-0">
                {opt}
              </span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

export default function PublicDemoPage() {
  const params = useParams();

  const [groupAgents, setGroupAgents] = useState<AgentDetailPublic[]>([]);
  const [groupAgentsLoading, setGroupAgentsLoading] = useState(true);

  const loadGroupAgents = async (group_id: string) => {
    try {
      setGroupAgentsLoading(true);
      setError(null);
      const data = await getDemoFlowsByGroupLink(group_id);
      setGroupAgents(data);
    } catch (err: any) {
      setError(
        err.response?.data?.detail ||
          err.message ||
          "Failed to load group agents",
      );
    } finally {
      setGroupAgentsLoading(false);
    }
  };

  useEffect(() => {
    if (!params.group_id) return;
    loadGroupAgents(params.group_id);
  }, [params.group_id]);

  const [selectedAgentId, setSelectedAgentId] = useState<string>("");
  const [agentDetail, setAgentDetail] = useState<AgentDetailPublic | null>(
    null,
  );

  useEffect(() => {
    if (!selectedAgentId) return;
    loadAgentDetail(selectedAgentId);
  }, [selectedAgentId]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [countryCode, setCountryCode] = useState("+91");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [variableValues, setVariableValues] = useState<Record<string, string>>(
    {},
  );
  const [calling, setCalling] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);

  // Prod services state
  const [servicesStartTime, setServicesStartTime] = useState("");
  const [startProdServicesLoading, setStartProdServicesLoading] =
    useState(false);
  const [prodServicesStatus, setProdServicesStatus] = useState<any>({});
  // Where Start Call goes, from the last prod status (VM or Kubernetes).
  const [teleserverUrl, setTeleserverUrl] = useState<string | undefined>();
  const [prodServicesStatusLoading, setProdServicesStatusLoading] =
    useState(false);
  // When to stop re-checking the status (see shouldPollProdServicesStatus).
  const [statusPoll, setStatusPoll] = useState({ until: 0, afterStart: false });

  // Update variables when plan changes
  useEffect(() => {
    if (agentDetail && agentDetail.is_multi_plan && selectedPlanId) {
      const selectedPlan = agentDetail.plans.find(
        (p) => p.id === selectedPlanId,
      );
      if (selectedPlan && selectedPlan.variables) {
        // Re-initialize variable values with plan-specific variables
        const initialValues: Record<string, string> = {};
        selectedPlan.variables.forEach((v: any) => {
          initialValues[v.name] = v.default_value || "";
        });
        setVariableValues(initialValues);
      }
    }
  }, [selectedPlanId, agentDetail]);

  const loadAgentDetail = async (agentId: string) => {
    try {
      setLoading(true);
      const data = await publicTestApi.getAgentDetail(agentId);
      setAgentDetail(data);

      // Auto-select default plan for multi-plan agents
      let defaultPlanId: string | null = null;
      if (data.is_multi_plan && data.plans.length > 0) {
        const defaultPlan =
          data.plans.find((p) => p.is_default) || data.plans[0];
        defaultPlanId = defaultPlan.id;
        setSelectedPlanId(defaultPlan.id);
      } else {
        setSelectedPlanId(null);
      }

      // Initialize variable values with defaults (from plan or agent-level)
      const initialValues: Record<string, string> = {};
      if (data.is_multi_plan && defaultPlanId) {
        // For multi-plan agents, use variables from the default plan
        const defaultPlan = data.plans.find((p) => p.id === defaultPlanId);
        if (defaultPlan && defaultPlan.variables) {
          defaultPlan.variables.forEach((v) => {
            initialValues[v.name] = v.default_value || "";
          });
        }
      } else {
        // For single-plan agents, use agent-level variables
        data.v2_variables.forEach((v) => {
          initialValues[v.name] = v.default_value || "";
        });
      }
      setVariableValues(initialValues);
    } catch (err: any) {
      showError(`Failed to load agent details: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const isCommonVariablesMode =
    agentDetail?.backend_json?.extra_config?.hide_plan_selection_in_demo;

  // Prod services handlers
  const handleStartProdServicesClick = async () => {
    if (!selectedAgentId) return;
    setStartProdServicesLoading(true);
    const server_version = "v2";
    let startFailed = false;
    try {
      const response = await startProdServices(selectedAgentId, server_version);
      // The API answers 200 with status "failed" when it rolled the start back.
      startFailed = response?.data?.status === "failed";
      if (startFailed) {
        showError("Couldn't start prod services. Please try again in a minute.");
      }
    } catch (err) {
      // Timed out or the connection dropped. The server often still finishes
      // the start, so keep checking the status.
      showWarning(
        "Prod services are taking longer than usual to start. The status updates automatically.",
      );
    }
    if (!startFailed) {
      setStatusPoll({
        until: Date.now() + STATUS_POLL_WINDOW_MS,
        afterStart: true,
      });
    }
    localStorage.setItem(
      `${selectedAgentId}_start_time`,
      new Date().toLocaleTimeString(),
    );
    setServicesStartTime(new Date().toLocaleTimeString());

    // Keep showing "Starting..." until the fresh status is in.
    await handleGetProdServicesStatus({ silent: true });
    setStartProdServicesLoading(false);
  };

  // silent: background re-check that keeps the last status on screen
  // instead of flashing "Loading...".
  const handleGetProdServicesStatus = useCallback(
    async ({ silent = false } = {}) => {
      if (!selectedAgentId) return;
      if (!silent) setProdServicesStatusLoading(true);
      const server_version = "v2";
      try {
        const res = await getProdServicesStatus(selectedAgentId, server_version);
        const url = teleserverUrlFromStatus(res, server_version);
        setTeleserverUrl(url);
        // The teleserver refuses connections while prod pods are still
        // coming up. That only means "not up yet", so keep the schedule
        // status and treat the services as not up.
        const res2 = await getProdServicesStatusFromTeleserver(
          selectedAgentId,
          server_version,
          url,
        ).catch(() => null);
        if (res?.data?.status === "success")
          setProdServicesStatus({
            ...res?.data?.message,
            services_status: res2?.data?.status,
            res2_message: res2?.data?.message,
            fetched_at: Date.now(),
          });
      } catch (err) {
        // getProdServicesStatus already logged it. Keep the last status.
      } finally {
        if (!silent) setProdServicesStatusLoading(false);
      }
    },
    [selectedAgentId],
  );

  // Load services start time from localStorage
  useEffect(() => {
    if (
      selectedAgentId &&
      localStorage.getItem(`${selectedAgentId}_start_time`)
    ) {
      setServicesStartTime(
        localStorage.getItem(`${selectedAgentId}_start_time`) || "",
      );
    }
  }, [selectedAgentId]);

  // Get prod services status when agent changes
  useEffect(() => {
    if (selectedAgentId) {
      setStatusPoll({
        until: Date.now() + STATUS_POLL_WINDOW_MS,
        afterStart: false,
      });
      handleGetProdServicesStatus();
    }
  }, [selectedAgentId, handleGetProdServicesStatus]);

  // Re-check while the services are starting, so the panel turns Up without
  // a manual refresh. The next check is scheduled only after the previous
  // one has landed.
  useEffect(() => {
    if (startProdServicesLoading || prodServicesStatusLoading) return;
    if (!shouldPollProdServicesStatus(prodServicesStatus, statusPoll)) return;
    const timer = setTimeout(
      () => handleGetProdServicesStatus({ silent: true }),
      STATUS_POLL_INTERVAL_MS,
    );
    return () => clearTimeout(timer);
  }, [
    prodServicesStatus,
    prodServicesStatusLoading,
    startProdServicesLoading,
    statusPoll,
    handleGetProdServicesStatus,
  ]);

  const handleInitiateCall = async () => {
    if (!agentDetail || !phoneNumber) {
      showError("Please select an agent and enter a phone number");
      return;
    }

    // Validate phone number
    const cleanPhone = phoneNumber.replace(/[^\d]/g, "");
    if (cleanPhone.length < 10) {
      showError("Please enter a valid phone number");
      return;
    }

    // Combine country code with phone number
    const fullPhoneNumber = countryCode + cleanPhone;

    // Validate required variables
    if (agentDetail) {
      // Get variables: use v2_variables for common mode, plan variables otherwise
      let variablesToValidate = agentDetail.v2_variables;
      if (
        !isCommonVariablesMode &&
        agentDetail.is_multi_plan &&
        selectedPlanId
      ) {
        const selectedPlan = agentDetail.plans.find(
          (p) => p.id === selectedPlanId,
        );
        if (selectedPlan && selectedPlan.variables) {
          variablesToValidate = selectedPlan.variables;
        }
      }

      const missingRequired = variablesToValidate
        .filter((v) => v.is_required && !variableValues[v.name])
        .map((v) => v.name);

      if (missingRequired.length > 0) {
        showError(
          `Please fill in required fields: ${missingRequired.join(", ")}`,
        );
        return;
      }
    }

    const autoStopWarning = getAutoStopWarning(
      prodServicesStatus,
      prodServicesStatusLoading,
    );
    // Same refresh as the status panel's button. Keep it after
    // getAutoStopWarning, which never blocks while a refresh loads.
    handleGetProdServicesStatus();
    if (autoStopWarning) {
      showWarning(autoStopWarning);
      return;
    }

    try {
      setCalling(true);
      const response: OutboundCallResponse =
        await publicCallingApi.startDemoCall(
          {
            agent_id: agentDetail.id,
            phone_number: fullPhoneNumber,
            variable_values: variableValues,
            plan_id: isCommonVariablesMode
              ? undefined
              : selectedPlanId || undefined,
          },
          teleserverUrl,
        );

      showSuccess("Call Started!");
    } catch (err: any) {
      showError(
        `Failed to initiate call: ${err.response?.data?.detail || err.message}`,
      );
    } finally {
      setCalling(false);
    }
  };

  if (loading) {
    return (
      <>
        <SmallHeader selectedFlowId={params.agent_id} />
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 py-8 px-4 p-24 mt-16">
          <div className="max-w-4xl mx-auto">
            {/* Header */}
            <div className="text-center mb-8">
              <h1 className="text-2xl font-bold mb-4 text-slate-700">
                Demo Call
              </h1>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
              {/* Agent Selection */}
              <div className="mb-5">
                <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                  Agent *
                </label>
                <select
                  value={selectedAgentId || ""}
                  onChange={(e) => setSelectedAgentId(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg text-lg dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none">
                  <option value="" disabled>
                    {groupAgentsLoading ? "Loading..." : "Please select agent"}
                  </option>
                  {Array.isArray(groupAgents) ? (
                    groupAgents.map((agent, index) => (
                      <option key={agent.id} value={agent.id}>
                        {agent.name}
                      </option>
                    ))
                  ) : (
                    <span>No active agents available for this url</span>
                  )}
                </select>
              </div>
              {selectedAgentId && (
                <div className="text-center">
                  <div className="text-xl mb-2">Loading...</div>
                </div>
              )}
            </div>
          </div>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 to-pink-100 dark:from-gray-900 dark:to-gray-800">
        <div className="text-center max-w-md p-8 bg-white dark:bg-gray-800 rounded-lg shadow-lg">
          <div className="text-6xl mb-4">❌</div>
          <h1 className="text-2xl font-bold mb-2">Group Not Found</h1>
          <p className="text-gray-600 dark:text-gray-400 mb-4">{error}</p>
          <p className="text-sm text-gray-500">
            Please check your URL or contact your administrator.
          </p>
        </div>
      </div>
    );
  }

  // if (!groupInfo) {
  //   return null;
  // }

  return (
    <>
      <SmallHeader selectedFlowId={params.agent_id} />
      <div className="flex flex-col min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 mt-16">
        {/* Main Content */}
        <div className="flex-1 py-8 px-4 overflow-y-auto">
          <div className="max-w-4xl mx-auto">
            {/* Header */}
            <div className="text-center mb-8">
              <h1 className="text-2xl font-bold mb-4 text-slate-700 dark:text-slate-300">
                Demo Call
              </h1>
            </div>

            {
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
                {/* Agent Selection */}
                <div className="mb-5">
                  <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                    Agent *
                  </label>
                  <select
                    value={selectedAgentId || ""}
                    onChange={(e) => setSelectedAgentId(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg text-lg dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none">
                    <option value="" disabled>
                      Please select agent
                    </option>
                    {Array.isArray(groupAgents) ? (
                      groupAgents.map((agent, index) => (
                        <option key={agent.id} value={agent.id}>
                          {agent.name}
                        </option>
                      ))
                    ) : (
                      <span>No active agents available for this url</span>
                    )}
                  </select>
                </div>

                {/* Prod Services Section */}
                {selectedAgentId && (
                  <div className="mb-5 border border-gray-300 dark:border-gray-600 flex flex-col gap-2 p-3 rounded bg-gray-100 dark:bg-gray-700">
                    <div className="flex gap-2 items-center">
                      <div className="text-nowrap border-r border-gray-400 dark:border-gray-500 pr-4">
                        <button
                          disabled={startProdServicesLoading}
                          onClick={handleStartProdServicesClick}
                          className="bg-gray-800 hover:bg-gray-700 disabled:bg-gray-300 text-gray-100 px-3 py-2 rounded cursor-pointer whitespace-nowrap text-sm font-medium">
                          {startProdServicesLoading
                            ? "Starting..."
                            : "Start Prod Services"}
                        </button>
                      </div>

                      <div className="flex flex-col pl-2 gap-1 w-full">
                        <div className="text-sm flex items-center gap-1 w-full text-gray-700 dark:text-gray-300">
                          <div>
                            Status:{" "}
                            {startProdServicesLoading &&
                            !isProdServicesUp(prodServicesStatus) ? (
                              <span>Starting...</span>
                            ) : prodServicesStatusLoading ? (
                              <span>Loading...</span>
                            ) : prodServicesStatus?.schedule_status ===
                              "Not Found" ? (
                              <span>Down</span>
                            ) : isProdServicesUp(prodServicesStatus) ? (
                              <span>Up</span>
                            ) : prodServicesStatus?.schedule_status ===
                              "Found" ? (
                              <span>Starting...</span>
                            ) : (
                              <span>Unknown</span>
                            )}
                          </div>

                          <button
                            disabled={prodServicesStatusLoading}
                            onClick={() => {
                              handleGetProdServicesStatus();
                            }}
                            className="ml-auto bg-gray-300 dark:bg-gray-600 rounded hover:bg-gray-400 dark:hover:bg-gray-500 disabled:bg-gray-300 dark:disabled:bg-gray-600 cursor-pointer flex items-center justify-center h-5 w-5">
                            {prodServicesStatusLoading ? (
                              "..."
                            ) : (
                              <RefreshIcon />
                            )}
                          </button>
                        </div>
                        {startProdServicesLoading ? (
                          <div className="mr-1 text-xs text-gray-600 dark:text-gray-400 flex items-center gap-1">
                            <LoadingSpinner width={14} height={14} />
                            <span>
                              Starting prod services. This can take up to 2
                              minutes, please keep this page open.
                            </span>
                          </div>
                        ) : prodServicesStatusLoading ? (
                          <div className="h-[2.25rem]"></div>
                        ) : prodServicesStatus?.schedule_status ===
                          "Not Found" ? (
                          <>
                            <div className="mr-1 text-xs text-gray-600 dark:text-gray-400">
                              Services start in: NA
                            </div>
                            <div className="mr-1 text-xs text-gray-600 dark:text-gray-400">
                              Services stop in: NA
                            </div>
                          </>
                        ) : (
                          prodServicesStatus?.schedule_status === "Found" && (
                            <>
                              <div className="mr-1 text-xs text-gray-600 dark:text-gray-400">
                                {prodServicesStatus?.services_status ? (
                                  <span>Services are up</span>
                                ) : prodServicesStatus?.time_left_to_start_services !==
                                  "Services are up" ? (
                                  `Services start in: ${prodServicesStatus?.time_left_to_start_services} (approx)`
                                ) : (
                                  `Taking longer than usual. Services will start in some time`
                                )}
                              </div>
                              <div className="mr-1 text-xs text-gray-600 dark:text-gray-400">
                                {`Services stop in: ${prodServicesStatus?.time_left_to_stop_prod}`}
                              </div>
                            </>
                          )
                        )}
                      </div>
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      {servicesStartTime
                        ? `After starting initially, please wait 15 minutes for services to start. Please click on the button again within 45 minutes post that, if you wish to continue using prod services.`
                        : ""}
                    </div>
                  </div>
                )}

                {/* Call Form */}
                {agentDetail && (
                  <div className="space-y-5">
                    <div>
                      <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                        Phone Number *
                      </label>
                      <div className="flex gap-2">
                        <select
                          value={countryCode}
                          onChange={(e) => setCountryCode(e.target.value)}
                          className="px-3 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                          style={{ width: "120px" }}>
                          <option value="+91">IN +91</option>
                          <option value="+1">US +1</option>
                          <option value="+44">UK +44</option>
                          <option value="+971">AE +971</option>
                          <option value="+65">SG +65</option>
                          <option value="+61">AU +61</option>
                          <option value="+81">JP +81</option>
                          <option value="+86">CN +86</option>
                          <option value="+49">DE +49</option>
                          <option value="+33">FR +33</option>
                          <option value="+39">IT +39</option>
                          <option value="+34">ES +34</option>
                          <option value="+7">RU +7</option>
                          <option value="+55">BR +55</option>
                          <option value="+52">MX +52</option>
                          <option value="+27">ZA +27</option>
                          <option value="+82">KR +82</option>
                          <option value="+66">TH +66</option>
                          <option value="+60">MY +60</option>
                          <option value="+62">ID +62</option>
                          <option value="+63">PH +63</option>
                          <option value="+92">PK +92</option>
                          <option value="+880">BD +880</option>
                          <option value="+94">LK +94</option>
                        </select>
                        <input
                          type="tel"
                          value={phoneNumber}
                          onChange={(e) =>
                            setPhoneNumber(
                              e.target.value.replace(/[^0-9]/g, ""),
                            )
                          }
                          placeholder="9876543210"
                          className="flex-1 px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg text-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none dark:bg-gray-700 dark:text-white"
                        />
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">
                        Full number: {countryCode}
                        {phoneNumber || "XXXXXXXXXX"}
                      </p>
                    </div>

                    {/* Plan Selection for Multi-Plan Agents */}
                    {agentDetail &&
                      agentDetail.is_multi_plan &&
                      agentDetail.plans.length > 0 &&
                      !isCommonVariablesMode && (
                        <div>
                          <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                            Select Plan *
                          </label>
                          <select
                            value={selectedPlanId || ""}
                            onChange={(e) => setSelectedPlanId(e.target.value)}
                            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg text-lg dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none">
                            {agentDetail.plans.map((plan) => (
                              <option key={plan.id} value={plan.id}>
                                {plan.name} {plan.is_default ? "(Default)" : ""}
                              </option>
                            ))}
                          </select>
                          {agentDetail.plans.find(
                            (p) => p.id === selectedPlanId,
                          )?.description && (
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">
                              {
                                agentDetail.plans.find(
                                  (p) => p.id === selectedPlanId,
                                )?.description
                              }
                            </p>
                          )}
                        </div>
                      )}

                    {/* Variable Inputs - show plan-specific variables for multi-plan agents */}
                    {agentDetail &&
                      (() => {
                        // Get variables: use v2_variables for common mode, plan variables otherwise
                        let displayVariables = agentDetail?.v2_variables;
                        if (
                          !isCommonVariablesMode &&
                          agentDetail.is_multi_plan &&
                          selectedPlanId
                        ) {
                          const selectedPlan = agentDetail.plans.find(
                            (p) => p.id === selectedPlanId,
                          );
                          if (selectedPlan && selectedPlan.variables) {
                            displayVariables = selectedPlan.variables;
                          }
                        }
                        return displayVariables.length > 0;
                      })() && (
                        <div className="border-t border-gray-200 dark:border-gray-700 pt-5">
                          <h3 className="text-base font-semibold mb-4 text-gray-900 dark:text-gray-100">
                            Call Configuration
                            {!isCommonVariablesMode &&
                              agentDetail.is_multi_plan &&
                              selectedPlanId && (
                                <span className="ml-2 text-sm font-normal text-blue-600 dark:text-blue-400">
                                  (
                                  {
                                    agentDetail.plans.find(
                                      (p) => p.id === selectedPlanId,
                                    )?.name
                                  }
                                  )
                                </span>
                              )}
                          </h3>
                          <div className="space-y-4">
                            {(() => {
                              // Get variables: use v2_variables for common mode, plan variables otherwise
                              let displayVariables = agentDetail?.v2_variables;
                              if (
                                !isCommonVariablesMode &&
                                agentDetail.is_multi_plan &&
                                selectedPlanId
                              ) {
                                const selectedPlan = agentDetail.plans.find(
                                  (p) => p.id === selectedPlanId,
                                );
                                if (selectedPlan && selectedPlan.variables) {
                                  displayVariables = selectedPlan.variables;
                                }
                              }
                              return displayVariables.filter(
                                (v) =>
                                  v.source !== "computed" &&
                                  v.source !== "session",
                              );
                            })().map((variable: any) => (
                              <div key={variable.name}>
                                <label className="block text-sm font-medium mb-1.5 text-gray-700 dark:text-gray-300">
                                  {variable.name}
                                  {variable.is_required && (
                                    <span className="text-red-500 ml-1">*</span>
                                  )}
                                  <span className="ml-2 text-xs text-gray-500">
                                    ({variable.variable_type}
                                    {variable.variable_type === "select" &&
                                      variable.options?.multiple &&
                                      ", multiple"}
                                    )
                                  </span>
                                </label>
                                {variable.description && (
                                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1.5">
                                    {variable.description}
                                  </p>
                                )}

                                {/* Render different input types based on variable_type */}
                                {variable.variable_type === "select" &&
                                variable.options?.values &&
                                variable.options.multiple ? (
                                  <MultiSelectDropdown
                                    options={variable.options.values}
                                    value={variableValues[variable.name] ?? ""}
                                    placeholder={`-- Select ${variable.name} --`}
                                    onChange={(value) =>
                                      setVariableValues({
                                        ...variableValues,
                                        [variable.name]: value,
                                      })
                                    }
                                  />
                                ) : variable.variable_type === "select" &&
                                  variable.options?.values ? (
                                  <select
                                    value={variableValues[variable.name] ?? ""}
                                    onChange={(e) =>
                                      setVariableValues({
                                        ...variableValues,
                                        [variable.name]: e.target.value,
                                      })
                                    }
                                    className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none">
                                    <option value="">
                                      -- Select {variable.name} --
                                    </option>
                                    {variable.options.values.map(
                                      (opt: string) => (
                                        <option key={opt} value={opt}>
                                          {opt}
                                        </option>
                                      ),
                                    )}
                                  </select>
                                ) : variable.variable_type === "date" ? (
                                  <input
                                    type="text"
                                    placeholder="YYYY-MM-DD"
                                    value={variableValues[variable.name] ?? ""}
                                    onChange={(e) =>
                                      setVariableValues({
                                        ...variableValues,
                                        [variable.name]: e.target.value,
                                      })
                                    }
                                    className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                                  />
                                ) : variable.variable_type === "boolean" ? (
                                  <select
                                    value={
                                      variableValues[variable.name] ?? "false"
                                    }
                                    onChange={(e) =>
                                      setVariableValues({
                                        ...variableValues,
                                        [variable.name]: e.target.value,
                                      })
                                    }
                                    className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none">
                                    <option value="false">False</option>
                                    <option value="true">True</option>
                                  </select>
                                ) : variable.variable_type === "number" ? (
                                  <input
                                    type="number"
                                    value={variableValues[variable.name] ?? ""}
                                    onChange={(e) =>
                                      setVariableValues({
                                        ...variableValues,
                                        [variable.name]: e.target.value,
                                      })
                                    }
                                    placeholder={
                                      variable.default_value ||
                                      `Enter ${variable.name}`
                                    }
                                    className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                                  />
                                ) : (
                                  <AutoResizeTextarea
                                    value={variableValues[variable.name] ?? ""}
                                    onChange={(e) =>
                                      setVariableValues({
                                        ...variableValues,
                                        [variable.name]: e.target.value,
                                      })
                                    }
                                    placeholder={
                                      variable.default_value ||
                                      `Enter ${variable.name}`
                                    }
                                    className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none resize-y block"
                                  />
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                  </div>
                )}
              </div>
            }
          </div>
        </div>

        {/* Fixed Footer */}
        {agentDetail && (
          <div className="sticky bottom-0 flex justify-center px-4">
            <div className="max-w-4xl w-full bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 shadow-lg rounded px-6 py-3 flex justify-center">
              <button
                onClick={handleInitiateCall}
                disabled={calling || !phoneNumber}
                className="px-8 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold rounded-lg hover:from-blue-700 hover:to-indigo-700 disabled:from-gray-400 disabled:to-gray-400 disabled:cursor-not-allowed transition-all shadow-md text-base w-full">
                {calling ? "Initiating Call..." : "Start Call"}
              </button>
            </div>
          </div>
        )}

        <Toaster />
      </div>
    </>
  );
}
